import { useQuery } from '@tanstack/react-query';
import { getBootState } from '@/lib/api';
import type { BootState } from '@/lib/api';

export function bootStatePollingInterval(state: BootState | undefined): 1000 | false {
  if (!state) return 1000;
  return state.phase === 'starting' || state.phase === 'verifying' ? 1000 : false;
}

/**
 * Polls `/api/ready` until the platform settles into a terminal phase
 * (`ready`, `degraded`, or `failed`). Used by `<BootGate />` to block
 * router mount until the server confirms all subsystems are up.
 *
 * - While phase is `starting` or `verifying`, refetches every 1s so the
 *   per-check progress visible in the splash updates in near-real-time.
 * - Once the phase is terminal, polling stops (refetchInterval returns
 *   false). No cookie required — the endpoint is public.
 * - A fetch failure (network down, server crashed mid-boot) surfaces
 *   via `isError` so the splash can render a recoverable error state.
 */
export function useBootState(): {
  data: BootState | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
} {
  const query = useQuery<BootState>({
    queryKey: ['boot-state'],
    queryFn: getBootState,
    // TanStack Query v5 passes the Query instance; pull data off its state.
    refetchInterval: q => {
      return bootStatePollingInterval(q.state.data);
    },
    refetchIntervalInBackground: true,
    staleTime: 0,
    retry: 3,
    retryDelay: attempt => Math.min(1000 * 2 ** attempt, 8000),
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
}
